import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { customFetch } from "../data/custom-fetch";

export function formatToCheckinDate(d: Date | string): string {
  if (d instanceof Date) {
    return dayjs(d).format("DD/MM/YYYY");
  }
  if (typeof d === "string") {
    const decoded = decodeURIComponent(d).trim();
    if (decoded.includes("/")) {
      const parts = decoded.split("/");
      if (parts.length === 3) {
        const p0 = parseInt(parts[0], 10);
        const p1 = parseInt(parts[1], 10);
        if (p1 > 12) {
          // MM/DD/YYYY -> DD/MM/YYYY
          return `${parts[1].padStart(2, "0")}/${parts[0].padStart(2, "0")}/${parts[2]}`;
        } else if (p0 > 12) {
          // Already DD/MM/YYYY
          return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
        } else {
          // Ambiguous: standard convention is DD/MM/YYYY
          return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
        }
      }
    } else if (decoded.includes("-")) {
      const parts = decoded.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          // YYYY-MM-DD -> DD/MM/YYYY
          return `${parts[2].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[0]}`;
        } else if (parts[2].length === 4) {
          // DD-MM-YYYY -> DD/MM/YYYY
          return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
        }
      }
    }
    return decoded;
  }
  return String(d);
}

export const formatToUsCheckinDate = formatToCheckinDate;

export function getDefaultCheckinDate(): string {
  return dayjs().add(60, "day").format("DD/MM/YYYY");
}

export function ratesLink(checkin?: string, nights: number = 2) {
  const checkinVal = checkin ? formatToCheckinDate(checkin) : getDefaultCheckinDate();
  const params = new URLSearchParams();
  params.set("currency", "USD");
  params.set("checkin", checkinVal);
  params.set("nights", String(nights));
  return `/rates?${params.toString()}`;
}

export function ratesLinkForRoomType(
  hotelId?: string,
  roomTypeId?: string,
  checkin?: Date | string,
  nights: number = 2,
) {
  const checkinVal = checkin ? formatToCheckinDate(checkin) : getDefaultCheckinDate();
  const params = new URLSearchParams();
  params.set("currency", "USD");
  params.set("checkin", checkinVal);
  params.set("nights", String(nights));
  if (roomTypeId) params.set("roomTypeId", roomTypeId);
  if (hotelId) params.set("idtokenprovider", hotelId);
  return `/rates?${params.toString()}`;
}

export async function fetchBestCheckinDate(
  hotelId?: string,
  nights: number = 2,
): Promise<{ bestCheckinDate: string; found: boolean }> {
  try {
    const params = new URLSearchParams();
    if (hotelId) params.set("hotelId", hotelId);
    params.set("nights", String(nights));
    const url = `/best-checkin-date?${params.toString()}`;
    const response = await customFetch<{
      bestCheckinDate: string;
      found: boolean;
    }>(url);
    return response;
  } catch (error) {
    return {
      bestCheckinDate: getDefaultCheckinDate(),
      found: false,
    };
  }
}

export function useBestCheckinDate(hotelId?: string, nights: number = 2): string {
  const query = useQuery({
    queryKey: ["best-checkin-date", hotelId, nights],
    queryFn: async () => {
      const res = await fetchBestCheckinDate(hotelId, nights);
      return res?.bestCheckinDate || getDefaultCheckinDate();
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: getDefaultCheckinDate(),
  });
  return query.data || getDefaultCheckinDate();
}

export function useRatesLink(hotelId?: string, nights: number = 2): string {
  const checkin = useBestCheckinDate(hotelId, nights);
  return ratesLink(checkin, nights);
}

export function useRatesLinkForRoomType(
  hotelId?: string,
  roomTypeId?: string,
  checkinDate?: Date | string,
  nights: number = 2,
): string {
  const bestDate = useBestCheckinDate(hotelId, nights);
  const checkin = checkinDate || bestDate;
  return ratesLinkForRoomType(hotelId, roomTypeId, checkin, nights);
}
